import { FormInput, FormSelector, Modal, Toast } from "@vesatogo/grass-core"
import { FormDateInput } from "@vesatogo/grass-dates"
import { FileUpload } from "@vesatogo/grass-dropzone"
import classNames from "classnames"
import dayjs from "dayjs"
import toast from "react-hot-toast"
import { OperationContext } from "urql"
import { useAddUpdateAgreementTransactionsMutation } from "~/generated/graphql"

export type TransactionFormProps = {
  isOpen: boolean
  onClose: () => void
  getInputProps: (key: string) => any
  getFileUploadProps: (key: string, maxFiles?: number) => any
  setFieldValue: (field: string, value: any) => void
  uploaderClassName?: string
  agreementId: string
  attachments: any[]
  refetch?: (opts?: Partial<OperationContext> | undefined) => void
  reset?: () => void
}

export const TransactionForm: React.FC<TransactionFormProps> = ({
  isOpen,
  onClose,
  getInputProps,
  getFileUploadProps,
  setFieldValue,
  uploaderClassName,
  agreementId,
  attachments,
  refetch,
  reset,
}) => {
  const [{ fetching: submitting }, addTransaction] =
    useAddUpdateAgreementTransactionsMutation()

  const onSubmit = async () => {
    const { error } = await addTransaction({
      agreementId: agreementId,
      input: {
        account_holder_name: getInputProps("account_holder_name").value,
        account_number: getInputProps("account_number").value,
        ifsc_code: getInputProps("ifsc_code").value,
        bank_name: getInputProps("bank").value,
        mode_of_payment: getInputProps("payment_mode").value?.name,
        amount: getInputProps("amount").value
          ? Number(getInputProps("amount").value)
          : 0,
        transaction_id: getInputProps("transaction_id").value,
        attachments_ids: attachments?.map((attachment: any) => attachment.id),
        transaction_date: getInputProps("transaction_date").value
          ? dayjs(getInputProps("transaction_date").value).format("YYYY-MM-DD")
          : undefined,
      },
    })

    if (error) {
      toast.custom(
        <Toast title="Error" subtitle={error.message} intent="danger" />
      )
    } else {
      toast.custom(
        <Toast title="Success" subtitle="Transaction added successfully" />
      )
      onClose?.()
      reset?.()
      refetch?.({
        requestPolicy: "network-only",
      })
    }
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transaction Details"
      bodyClassName="max-w-3xl overflow-visible"
      primaryActionButtonProps={{
        text: "Submit",
        onClick: onSubmit,
        loading: submitting,
      }}
      secondaryActionButtonProps={{
        text: "Cancel",
        onClick: onClose,
        variant: "outline",
        disabled: submitting,
      }}
    >
      <div className="flex flex-col gap-4">
        <div className={classNames("", uploaderClassName)}>
          <FileUpload
            {...getFileUploadProps("transaction_attachments", 1)}
            media={attachments}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            appName="setutrade"
            className="h-full aspect-square"
            onSuccess={(file: any) => {
              setFieldValue("transaction_attachments", [
                ...attachments,
                file[0]?.data?.media,
              ])
            }}
          />
        </div>
        <div className="col-span-8 grid grid-cols-2 gap-4">
          <FormInput
            {...getInputProps("account_holder_name")}
            label="Account holders name"
            placeholder="Suresh M. Kumar"
          />
          <FormInput
            {...getInputProps("account_number")}
            label="Account Number"
            placeholder="1234567890"
          />
          <FormInput
            {...getInputProps("ifsc_code")}
            label="IFSC Code"
            placeholder="SBI00056423"
          />
          <FormInput
            {...getInputProps("bank")}
            label="Bank"
            placeholder="State Bank of India"
          />
          <FormSelector
            {...getInputProps("payment_mode")}
            label="Payment Mode"
            required
            options={[
              { id: "UPI", name: "UPI" },
              { id: "NEFT", name: "NEFT" },
              { id: "RTGS", name: "RTGS" },
              { id: "IMPS", name: "IMPS" },
            ]}
            placeholder="NEFT"
          />
          <FormInput
            {...getInputProps("amount")}
            label="Amount"
            required
            placeholder="₹ 10,200"
          />
          <FormInput
            {...getInputProps("transaction_id")}
            label="Transaction ID"
            required
            placeholder="2398xxxxxxxxxx"
          />
          <FormDateInput
            {...getInputProps("transaction_date")}
            label="Transaction Date"
            required
            placeholder="2022-01-01"
          />
        </div>
      </div>
    </Modal>
  )
}
